// File Metadata:
// Filename: usersApiSlice.js
// Version: 1.00
// Created: 2025-03-28 19:41:58
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { createSelector, createEntityAdapter } from '@reduxjs/toolkit'
import { apiSlice } from '../../app/api/apiSlice'

const usersAdapter = createEntityAdapter({})
const initialState = usersAdapter.getInitialState()

export const usersApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUsers: builder.query({
            query: () => '/users',
            transformResponse: responseData => {
                const loadedUsers = responseData.map(user => {
                    user.id = user._id
                    return user
                })
                return usersAdapter.setAll(initialState, loadedUsers)
            },
            providesTags: (result) =>
                result
                    ? [
                          { type: 'User', id: 'LIST' },
                          ...result.ids.map(id => ({ type: 'User', id })),
                      ]
                    : [{ type: 'User', id: 'LIST' }],
        }),

        getUserById: builder.query({
            query: id => `/users/${id}`,
            providesTags: (result, error, id) => [{ type: 'User', id }]
        }),

        getUserProfile: builder.query({
            query: (userId) => `/users/${userId}/profile`,
            transformResponse: (response) => {
                if (response.crew?.startDate) {
                    response.crew.startDate = new Date(response.crew.startDate)
                }
                if (response.competencies) {
                    response.competencies = response.competencies.map(comp => ({
                        ...comp,
                        certifiedDate: new Date(comp.certifiedDate),
                        expiryDate: comp.expiryDate ? new Date(comp.expiryDate) : null
                    }))
                }
                return response
            },
            providesTags: (result, error, userId) => [{ type: 'User', id: userId }]
        }),

        updateUser: builder.mutation({
            query: initialUserData => ({
                url: '/users',
                method: 'PATCH',
                body: initialUserData
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'User', id: arg.id }
            ]
        }),

        deleteUser: builder.mutation({
            query: ({ id }) => ({
                url: '/users',
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'User', id: arg.id }
            ]
        }),

        updateUserProfile: builder.mutation({
            query: ({ userId, ...data }) => ({
                url: `/users/${userId}/profile`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }]
        }),

        updateUserCrew: builder.mutation({
            query: ({ userId, ...data }) => ({
                url: `/users/${userId}/crew`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }]
        }),

        updateUserCompetency: builder.mutation({
            query: ({ userId, competencyId, ...data }) => ({
                url: `/users/${userId}/competencies/${competencyId}`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }]
        }),

        addNewUser: builder.mutation({
            query: initialUserData => ({
                url: '/users',
                method: 'POST',
                body: {
                    ...initialUserData
                }
            }),
            invalidatesTags: [{ type: 'User', id: 'LIST' }]
        })
    })
})

export const { useGetUsersQuery,
    useGetUserByIdQuery,
    useGetUserProfileQuery,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useUpdateUserProfileMutation,
    useUpdateUserCrewMutation,
    useUpdateUserCompetencyMutation,
    useAddNewUserMutation } = usersApiSlice

// returns the query result object
export const selectUsersResult = usersApiSlice.endpoints.getUsers.select()

// creates memoized selector
const selectUsersData = createSelector(
	selectUsersResult,
	usersResult => usersResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
	selectAll: selectAllUsers,
	selectById: selectUserById,
	selectIds: selectUserIds
	// Pass in a selector that returns the users slice of state
} = usersAdapter.getSelectors(state => selectUsersData(state) ?? initialState)
